import type { MembershipCardChangeFailure } from '../domain/cardChange';
import { upstreamField } from '../domain/errors';
import type {
  MembershipManageError,
  MembershipManageIncident,
} from '../domain/manage';

/** What the "Add card" sheet (`?modal=club-card`) does about a failed card change or confirmation. */
export interface CardChangeErrorOutcome {
  incident: MembershipManageIncident | null;
  /** `reconcile` asks `/me` before settling on `failure`; nothing is ever re-sent. */
  next: 'fail' | 'reconcile';
  failure: MembershipCardChangeFailure;
  /** The membership changed under us (404, 409); re-read it. */
  refresh: boolean;
  /** No answer or a server fault: the backend may have taken the card and started a charge. */
  unknown: boolean;
}

/**
 * Classifies a failed `/me/card` or `/me/card/confirm`: a refused card and a
 * request the payment service refused (`MEMBERSHIP_PAYMENT_REJECTED`, nothing charged) fail at once
 * with a new form; 401, 404 and 409 fail quietly; anything else — a 503, no
 * answer, a failed confirmation — is checked through `/me`. Only a 503 or no
 * answer is `unknown`: any other 4xx means nothing was taken.
 */
export const cardChangeErrorOutcome = (
  stage: 'submit' | 'confirm',
  error: MembershipManageError,
): CardChangeErrorOutcome => {
  const { kind, status, code, upstream } = error;
  const reported: MembershipManageIncident = {
    reason: 'cardUpdateFailed',
    stage,
    kind,
    status,
    code,
    ...upstreamField(upstream),
  };
  switch (kind) {
    case 'card_refused':
      return {
        incident: { reason: 'cardRefused', flow: 'cardChange', status, code },
        next: 'fail',
        failure: 'card_refused',
        refresh: false,
        unknown: false,
      };
    case 'payment_rejected':
      return {
        incident: reported,
        next: 'fail',
        failure: 'payment_rejected',
        refresh: false,
        unknown: false,
      };
    case 'unauthorized':
      return {
        incident: null,
        next: 'fail',
        failure: 'not_confirmed',
        refresh: false,
        unknown: false,
      };
    case 'not_found':
    case 'not_cancellable':
    case 'plan_change_not_applicable':
    case 'upgrade_requires_payment':
    case 'conflict':
      return {
        incident: null,
        next: 'fail',
        failure: 'not_confirmed',
        refresh: true,
        unknown: false,
      };
    default:
      return {
        incident: reported,
        next: 'reconcile',
        failure: 'not_confirmed',
        refresh: false,
        unknown: kind === 'unknown_outcome',
      };
  }
};
